import axios from "axios";

/** Relative API path segment for franchise debt endpoints. */
export const FRANCHISE_DEBT = "franchise-debt";

/** Builds the absolute franchise-debt API base URL from env. */
function franchiseDebtBaseUrl() {
  const site = import.meta.env.REACT_APP_SITE_URL || "";
  return `${site}${FRANCHISE_DEBT}`;
}

/**
 * Fetches franchise debt record(s), optionally scoped to one franchise.
 * @param {string|number|null|undefined} franchiseId - Franchise id filter; omit or empty for unscoped list response.
 * @returns {import("axios").AxiosPromise} Axios GET for franchise debt.
 */
export function getFranchiseDebt(franchiseId) {
  const base = franchiseDebtBaseUrl();
  if (franchiseId == null || franchiseId === "") {
    return axios.get(base);
  }
  return axios.get(
    `${base}?franchise_id=${encodeURIComponent(String(franchiseId))}`,
  );
}

/**
 * Lists franchise debt with optional query parameters.
 * @param {Record<string, string|number|boolean|null|undefined>} [params={}] - Query key/values; empty values are omitted.
 * @returns {import("axios").AxiosPromise} Axios GET for the debt list.
 */
export function listFranchiseDebt(params = {}) {
  const base = franchiseDebtBaseUrl();
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === "" || value == null) return;
    sp.set(key, String(value));
  });
  const qs = sp.toString();
  return axios.get(qs ? `${base}?${qs}` : base);
}

/**
 * Creates a franchise debt configuration.
 * @param {FormData|Record<string, unknown>} formData - Payload for the create request.
 * @returns {import("axios").AxiosPromise} Axios POST response.
 */
export function createFranchiseDebt(formData) {
  return axios.post(franchiseDebtBaseUrl(), formData);
}

/**
 * Updates an existing franchise debt configuration.
 * @param {string|number} id - Debt record id.
 * @param {FormData|Record<string, unknown>} formData - Payload for the update request.
 * @returns {import("axios").AxiosPromise} Axios POST response.
 */
export function updateFranchiseDebt(id, formData) {
  return axios.post(`${franchiseDebtBaseUrl()}/${id}`, formData);
}

/**
 * Deletes a franchise debt configuration.
 * @param {string|number} id - Debt record id.
 * @returns {import("axios").AxiosPromise} Axios DELETE response.
 */
export function deleteFranchiseDebt(id) {
  return axios.delete(`${franchiseDebtBaseUrl()}/${id}`);
}

/**
 * Pauses collection for a franchise debt record.
 * @param {string|number} id - Debt record id.
 * @returns {import("axios").AxiosPromise} Axios POST response.
 */
export function pauseFranchiseDebt(id) {
  return axios.post(`${franchiseDebtBaseUrl()}/${id}/pause`);
}

/**
 * Resumes collection for a paused franchise debt record.
 * @param {string|number} id - Debt record id.
 * @returns {import("axios").AxiosPromise} Axios POST response.
 */
export function resumeFranchiseDebt(id) {
  return axios.post(`${franchiseDebtBaseUrl()}/${id}/resume`);
}

/**
 * Closes a franchise debt account (premature closure with audit fields).
 * @param {string|number} id - Debt record id.
 * @param {FormData|Record<string, unknown>} formData - Closure payload (e.g. reason, date).
 * @returns {import("axios").AxiosPromise} Axios POST response.
 */
export function closeFranchiseDebt(id, formData) {
  return axios.post(`${franchiseDebtBaseUrl()}/${id}/close`, formData);
}

/** Parses filename from a Content-Disposition header string. */
function parseFilenameFromContentDisposition(headerValue) {
  if (!headerValue || typeof headerValue !== "string") return null;
  const utf8 = /filename\*=UTF-8''([^;\s]+)/i.exec(headerValue);
  if (utf8 && utf8[1]) {
    try {
      return decodeURIComponent(utf8[1].replace(/^"+|"+$/g, "").trim());
    } catch {
      return utf8[1].replace(/^"+|"+$/g, "").trim();
    }
  }
  const ascii = /filename\s*=\s*("?)([^";\n]+)\1/i.exec(headerValue);
  if (ascii && ascii[2]) return ascii[2].trim();
  return null;
}

const CSV_MIME = "text/csv;charset=utf-8";

/** Returns whether a string looks like CSV body text. */
function looksLikeCsvText(s) {
  if (typeof s !== "string" || s.length < 2) return false;
  const t = s.replace(/^\uFEFF/, "");
  return t.includes("\n") && (t.includes(",") || t.includes(";"));
}

/** Tries to read a CSV string from common keys on a plain object. */
function tryExtractCsvFromPlainObject(obj) {
  if (!obj || typeof obj !== "object") return null;
  const keys = ["data", "csv", "content", "file", "body", "export"];
  for (let i = 0; i < keys.length; i += 1) {
    const v = obj[keys[i]];
    if (typeof v === "string" && looksLikeCsvText(v)) return v;
  }
  return null;
}

/** Normalizes API response data into a CSV Blob, or returns null if not possible. */
function toCsvDownloadBlob(data) {
  if (typeof data === "string") {
    const trimmed = data.trimStart();
    if (trimmed.startsWith("{")) {
      try {
        const obj = JSON.parse(data);
        const inner = tryExtractCsvFromPlainObject(obj);
        if (inner) return new Blob([inner], { type: CSV_MIME });
      } catch {
        /* treat as raw CSV */
      }
    }
    return new Blob([data], { type: CSV_MIME });
  }
  if (data !== null && typeof data === "object") {
    if (data instanceof Blob) {
      const t = (data.type || "").toLowerCase();
      if (t.includes("csv") || t.includes("text/plain") || t === "text/csv") {
        return data;
      }
      return new Blob([data], { type: CSV_MIME });
    }
    if (data instanceof ArrayBuffer) {
      return new Blob([data], { type: CSV_MIME });
    }
    if (!Array.isArray(data)) {
      const inner = tryExtractCsvFromPlainObject(data);
      if (inner) return new Blob([inner], { type: CSV_MIME });
    }
  }
  return null;
}

/**
 * Triggers a browser download from an export axios response containing CSV data.
 * @param {import("axios").AxiosResponse} response - Axios response with blob or CSV-like data.
 * @param {string} defaultFilename - Filename used when Content-Disposition is missing.
 * @returns {boolean} True if a download was started, false if the body could not be turned into CSV.
 */
export function downloadCsvExportResponse(response, defaultFilename) {
  const csvBlob = toCsvDownloadBlob(response?.data);
  if (!csvBlob) return false;

  const headers = response.headers || {};
  const cd =
    headers["content-disposition"] || headers["Content-Disposition"] || "";
  const fromHeader = parseFilenameFromContentDisposition(cd);
  const filename = fromHeader || defaultFilename;

  const url = URL.createObjectURL(csvBlob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
}

/**
 * Requests a franchise debt summary CSV export from the API.
 * @param {Record<string, string|number|boolean|null|undefined>} [params={}] - Filter query parameters.
 * @returns {import("axios").AxiosPromise<Blob>} Axios GET with blob response type.
 */
export function exportFranchiseDebtSummary(params = {}) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === "" || value == null) return;
    sp.set(key, String(value));
  });
  const qs = sp.toString();
  const path = `${franchiseDebtBaseUrl()}/export/summary`;
  return axios.get(qs ? `${path}?${qs}` : path, { responseType: "blob" });
}

/**
 * Requests a franchise debt ledger CSV export from the API.
 * @param {Record<string, string|number|boolean|null|undefined>} [params={}] - Filter query parameters.
 * @returns {import("axios").AxiosPromise<Blob>} Axios GET with blob response type.
 */
export function exportFranchiseDebtLedger(params = {}) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === "" || value == null) return;
    sp.set(key, String(value));
  });
  const qs = sp.toString();
  const path = `${franchiseDebtBaseUrl()}/export/ledger`;
  return axios.get(qs ? `${path}?${qs}` : path, { responseType: "blob" });
}
